import { Alert } from '@palmetto/palmetto-components';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';

import { MilestoneType, PostActivationPackageActions } from 'types';

import { useArePrecedingMilestonesCompleted } from '../../hooks/useArePrecedingMilestonesCompleted';
import { useGetAccountQuery } from '../../services/accounts';
import { AccountStatusIndicators } from '../Account/AccountStatusIndicators';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';
import { SkeletonBox } from '../SkeletonBox';
import AdminStipulationBannerList from '../Stipulations/AdminStipulationBannerList';

import { PostActivationPackage } from './PostActivationPackage';

export const PostActivationMilestonePackage = () => {
  const { id, action } = useParams<{ id: any; action: PostActivationPackageActions }>();
  const { data: account, isLoading: accountIsLoading } = useGetAccountQuery(id);
  const navigate = useNavigate();

  const canSubmitPostActivationPackage = useArePrecedingMilestonesCompleted(
    account?.milestones,
    MilestoneType.postActivation,
  );

  useEffect(() => {
    if (!action || !PostActivationPackageActions[action]) {
      navigate(`/accounts/${id}`, { replace: true });
    }
  }, [id, navigate, action]);

  return (
    <>
      <Helmet>
        <title>{`${account?.primaryApplicantName} Activation Package`}</title>
      </Helmet>
      <MainContainer>
        <AdminStipulationBannerList />
        <PageHeader
          eyebrow={
            <>
              <Link to="/accounts">Accounts</Link> \{' '}
              <Link to={`/accounts/${id}`}>{`${account?.primaryApplicantName}`}</Link>
            </>
          }
          title="Post Activation Package"
          description="Provide the required documents."
        />
        <AccountStatusIndicators account={account} />
        {account && action !== PostActivationPackageActions.review && !canSubmitPostActivationPackage && (
          <Alert
            variant="warning"
            hasIcon
            title="Activation Package is not complete"
            message="The Activation Package must be completed and approved before submitting the Post Activation Package."
          />
        )}
        {accountIsLoading ? (
          <FormSection title="Loading...">
            <SkeletonBox height="25px" width="100" />
          </FormSection>
        ) : (
          <PostActivationPackage canSubmitPostActivationPackage={canSubmitPostActivationPackage} />
        )}
      </MainContainer>
    </>
  );
};
